/**
 * WhatsApp API Client
 *
 * HTTP client for communicating with the WhatsApp backend service
 * Handles authentication, request/response formatting, and error handling
 */

import { getAccessToken } from '../lib/supabase';

const WHATSAPP_SERVICE_URL = import.meta.env.VITE_WHATSAPP_SERVICE_URL || 'http://localhost:3001';

/**
 * Connection status from backend
 */
export type ConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'qr_ready'
  | 'connected'
  | 'reconnecting';

/**
 * WhatsApp status response
 */
export interface WhatsAppStatus {
  connected: boolean;
  status: ConnectionStatus;
  phoneNumber?: string;
  name?: string;
  messagesSentToday?: number;
  reason?: string;
}

export interface WhatsAppChatSummary {
  jid: string;
  name: string;
  lastMessage?: WhatsAppMessage;
  unreadCount: number;
  updatedAt: number;
}

export type WhatsAppMessageDirection = 'incoming' | 'outgoing';
export type WhatsAppMessageStatus = 'pending' | 'sent' | 'error';
export type WhatsAppMediaType = 'image' | 'video' | 'audio' | 'document' | 'sticker' | 'contact' | 'location';

export interface Reaction {
  emoji: string;
  fromJid: string;
  fromName: string;
  timestamp: string;
}

export interface WhatsAppMessage {
  id: string;
  jid: string;
  text: string;
  timestamp: number;
  direction: WhatsAppMessageDirection;
  status?: WhatsAppMessageStatus;
  mediaUrl?: string | null;
  mediaType?: WhatsAppMediaType | null;
  reactions?: Reaction[];
}

export interface WhatsAppChatListResponse {
  chats: WhatsAppChatSummary[];
  lookbackHours: number;
}

export interface WhatsAppMessagesResponse {
  messages: WhatsAppMessage[];
  lookbackHours: number;
}

/**
 * Connection response
 */
export interface ConnectionResponse {
  status: ConnectionStatus;
  phoneNumber?: string;
  name?: string;
  message?: string;
}

/**
 * Disconnect response
 */
export interface DisconnectResponse {
  success: boolean;
  message: string;
}

/**
 * API Error
 */
export class WhatsAppAPIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public details?: any
  ) {
    super(message);
    this.name = 'WhatsAppAPIError';
  }
}

/**
 * Make authenticated request to WhatsApp service
 */
async function makeRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    // Get JWT token from Supabase session
    const token = await getAccessToken();

    if (!token) {
      throw new WhatsAppAPIError('Not authenticated', 401);
    }

    // Make request with auth header
    const response = await fetch(`${WHATSAPP_SERVICE_URL}/api/whatsapp${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    // Handle error responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new WhatsAppAPIError(
        errorData.message || `Request failed with status ${response.status}`,
        response.status,
        errorData
      );
    }

    // Parse and return JSON response
    return await response.json();
  } catch (error) {
    if (error instanceof WhatsAppAPIError) {
      throw error;
    }

    // Network or other errors
    throw new WhatsAppAPIError(
      error instanceof Error ? error.message : 'Unknown error occurred',
      undefined,
      error
    );
  }
}

/**
 * Connect to WhatsApp
 * Initiates connection and QR generation
 */
export async function connectWhatsApp(): Promise<ConnectionResponse> {
  return makeRequest<ConnectionResponse>('/connect', {
    method: 'POST',
  });
}

/**
 * Get WhatsApp connection status
 */
export async function getWhatsAppStatus(): Promise<WhatsAppStatus> {
  return makeRequest<WhatsAppStatus>('/status', {
    method: 'GET',
  });
}

export async function getWhatsAppChats(sinceHours?: number): Promise<WhatsAppChatListResponse> {
  const params = new URLSearchParams();
  if (sinceHours) {
    params.set('sinceHours', String(sinceHours));
  }
  const query = params.toString();
  const endpoint = query ? `/chats?${query}` : '/chats';
  return makeRequest<WhatsAppChatListResponse>(endpoint, { method: 'GET' });
}

export async function getWhatsAppMessages(
  jid: string,
  sinceHours?: number
): Promise<WhatsAppMessagesResponse> {
  const params = new URLSearchParams();
  if (sinceHours) {
    params.set('sinceHours', String(sinceHours));
  }
  const query = params.toString();
  const encodedJid = encodeURIComponent(jid);
  const endpoint = query
    ? `/chats/${encodedJid}/messages?${query}`
    : `/chats/${encodedJid}/messages`;
  return makeRequest<WhatsAppMessagesResponse>(endpoint, { method: 'GET' });
}

export async function sendWhatsAppMessage(
  jid: string,
  payload: { text: string; contactName?: string }
): Promise<{ message?: WhatsAppMessage }> {
  const encoded = encodeURIComponent(jid);
  return makeRequest<{ message?: WhatsAppMessage }>(`/chats/${encoded}/messages`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Send or remove a reaction to a message
 * @param messageId - WhatsApp message ID
 * @param emoji - Emoji string (e.g., '❤️') or empty string to remove reaction
 */
export async function sendReaction(
  messageId: string,
  emoji: string
): Promise<{ success: boolean; message: string }> {
  return makeRequest<{ success: boolean; message: string }>(`/messages/${messageId}/react`, {
    method: 'POST',
    body: JSON.stringify({ emoji }),
  });
}

/**
 * Disconnect from WhatsApp
 * Logs out and clears session
 */
export async function disconnectWhatsApp(): Promise<DisconnectResponse> {
  return makeRequest<DisconnectResponse>('/disconnect', {
    method: 'POST',
  });
}

/**
 * Get SSE stream URL for real-time updates
 * Note: Must manually add Authorization header when creating EventSource
 */
export function getSSEStreamURL(): string {
  return `${WHATSAPP_SERVICE_URL}/api/whatsapp/stream`;
}

/**
 * Health check (no auth required)
 */
export async function healthCheck(): Promise<{
  status: string;
  timestamp: string;
  activeSessions: number;
  sseClients: number;
}> {
  const response = await fetch(`${WHATSAPP_SERVICE_URL}/api/whatsapp/health`);

  if (!response.ok) {
    throw new Error('Health check failed');
  }

  return await response.json();
}

/**
 * Get full media URL for authenticated media access
 * Converts relative media path to absolute URL with proper base
 */
export function getMediaUrl(mediaPath: string): string {
  // If it's already a full URL, return as-is
  if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://')) {
    return mediaPath;
  }

  // Remove leading slash if present
  const path = mediaPath.startsWith('/') ? mediaPath.slice(1) : mediaPath;

  // Construct full URL using the WhatsApp service base URL
  return `${WHATSAPP_SERVICE_URL}/${path}`;
}

/**
 * Fetch media with authentication and return as blob URL
 * This is necessary because <img> tags don't send auth headers
 */
export async function fetchAuthenticatedMedia(mediaPath: string): Promise<string> {
  try {
    const token = await getAccessToken();

    if (!token) {
      throw new WhatsAppAPIError('Not authenticated', 401);
    }

    const fullUrl = getMediaUrl(mediaPath);
    console.log('[fetchAuthenticatedMedia] Fetching:', fullUrl);
    console.log('[fetchAuthenticatedMedia] Token length:', token.length);

    const response = await fetch(fullUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    console.log('[fetchAuthenticatedMedia] Response status:', response.status);

    if (!response.ok) {
      throw new WhatsAppAPIError(
        `Failed to fetch media: ${response.statusText}`,
        response.status
      );
    }

    // Convert to blob and create object URL
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    if (error instanceof WhatsAppAPIError) {
      throw error;
    }
    throw new WhatsAppAPIError(
      error instanceof Error ? error.message : 'Failed to fetch media',
      undefined,
      error
    );
  }
}

// ===== Message Recovery API =====

/**
 * Message recovery response
 */
export interface MessageRecoveryResponse {
  success: boolean;
  message: string;
  details: {
    instanceName: string;
    instanceState: string;
    recoveryId: string;
    expectedSyncTime: string;
    statusCheck: {
      endpoint: string;
      method: string;
      description: string;
    };
    note: string;
  };
}

/**
 * Recovery status for a single recovery attempt
 */
export interface RecoveryStatus {
  recovery_id: string;
  requested_at: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  latency_seconds: number | null;
  total_messages: number | null;
  processed_messages: number | null;
  failed_messages: number | null;
  completion_percentage: number;
}

/**
 * Recovery status response
 */
export interface RecoveryStatusResponse {
  recoveries: RecoveryStatus[];
  totalCount: number;
}

/**
 * Manually trigger message recovery
 *
 * This restarts the WhatsApp instance, which causes Evolution API
 * to reconnect and fire the MESSAGES_SET webhook with all historical
 * messages (subject to sync window configuration).
 *
 * Use cases:
 * - User suspects messages were missed during a disconnect
 * - Manual sync after fixing webhook issues
 * - Recovery after network outage
 *
 * Rate limiting: 1 request per 60 seconds per user
 * Expected completion: 15-30 seconds for MESSAGES_SET to fire
 *
 * @returns Recovery initiation response with tracking details
 * @throws WhatsAppAPIError if recovery fails or rate limited
 */
export async function recoverMessages(): Promise<MessageRecoveryResponse> {
  return makeRequest<MessageRecoveryResponse>('/recover-messages', {
    method: 'POST',
  });
}

/**
 * Get status of recent message recovery requests
 *
 * Returns the last 10 recovery attempts with their completion status.
 * Useful for:
 * - Displaying recovery progress to user
 * - Showing recovery history
 * - Debugging recovery issues
 *
 * @returns List of recent recoveries with status and metrics
 */
export async function getRecoveryStatus(): Promise<RecoveryStatusResponse> {
  return makeRequest<RecoveryStatusResponse>('/recovery-status', {
    method: 'GET',
  });
}
